import { useState, useCallback } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { saveAs } from "file-saver";
import { useMutation } from "@apollo/client";
import styled from "styled-components";
import { format } from "date-fns";
import Cropper from "react-easy-crop";
import { useAuth } from "../Auth/AuthProvider";
import useBannerCSS from "../../utils/hooks/useBannerCSS";
import useCropImage from "../../utils/hooks/useCropImage";
import { ReactComponent as InfoIcon } from "../../assets/icon/info.svg";
import { ReactComponent as ArrowIcon } from "../../assets/icon/right-arrow.svg";
import { uploadBase64Image, downloadImage } from "../../utils/s3-images";
import { generateBannerOnCanvas } from "../../utils/images";
import CREATE_USER_BANNER from "../../server/graphql/mutations/create-user-banner";
import Loading from "../../components/Loading";
import { classNames, fixUrl } from "../../helpers";
import typography from "../../helpers/typography";
import Modal from "../../components/Modal";
import DownloadDpModal from "./DownloadDpModal";
import EditImageControls from "./EditImageControls";
import { PlusIcon } from "@heroicons/react/24/outline";
import { INCREMENT_EVENT_FIELDS } from "../../server/graphql/mutations/events";
import { Content } from "../../components/dashboard/ResizableShape";

const Description = styled.div`
  p {
    margin-bottom: 1.25rem;
  }
`;

const UploadIconOverlay = styled.div`
  align-items: center;
  background: radial-gradient(black, transparent);
  border-radius: ${(props) => (props.shape === "CIRCLE" ? "100%" : "")};
  display: flex;
  justify-content: center;
  height: 100%;
  &:hover {
    animation: pulse 1s infinite;
    animation-timing-function: linear;
  }
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }

  ${(props) => props.imageExist && "opacity: 50%;"};
`;

const OnUploadingOverlay = styled.div`
  align-items: center;
  background: radial-gradient(black, transparent);
  border-radius: ${(props) => (props.shape === "CIRCLE" ? "100%" : "")};
  display: flex;
  justify-content: center;
  position: absolute;
`;

const CreateUserBannerForm = ({ currentEvent, loading, error }) => {
  const [incrementEvent] = useMutation(INCREMENT_EVENT_FIELDS);

  const { user } = useAuth();
  const [bannerEl, setBannerEL] = useState(null);

  const bannerRef = useCallback((node) => {
    if (node !== null) {
      setBannerEL(node);
    }
  }, []);

  const { avatarCSS, usernameCSS } = useBannerCSS(bannerEl, currentEvent);

  const initialValuesState = {
    file: null,
    imagePreviewUrl: "",
    userName: "",
    displayPublicly: false,
    createBannerStatus: "idle",
    photoUploadStatus: "idle",
    imageUrl: "",
  };

  const [createUserBanner, { submitting = loading }] =
    useMutation(CREATE_USER_BANNER);

  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [values, setValues] = useState(initialValuesState);
  const [info, setInfo] = useState(false);
  const {
    crop,
    setCrop,
    setRotation,
    zoom,
    setZoom,
    croppedImage,
    avatarUrl,
    onCropComplete,
    showCroppedImage,
    setCroppedFile,
    rotation,
    setCroppedImage,
  } = useCropImage(values.imagePreviewUrl);

  const toggleInfoModal = () => setInfo((prevState) => !prevState);

  /**
   * Handles change of image input
   * @param {*} event change event
   */
  const handlePhotoUpload = (event) => {
    event.preventDefault();

    // set loader to true
    setValues((prevState) => ({
      ...prevState,
      photoUploadStatus: "pending",
      file: null,
    }));
    setCroppedImage(null);
    setCroppedFile(null);

    // handles uploaded file and stores in state
    let reader = new FileReader();
    let file = event.target.files[0];

    reader.onloadend = () => {
      setValues((prevState) => ({
        ...prevState,
        file: file,
        imagePreviewUrl: reader.result,
      }));
    };

    reader.readAsDataURL(file);

    setValues((prevState) => ({
      ...prevState,
      photoUploadStatus: "resolved",
    }));
  };

  /**
   * Handles change of text input for username and checkbox
   * @param {*} event change event
   */
  const handleChange = (event) => {
    const { name, value, checked } = event.target;

    setValues((prevState) => ({
      ...prevState,
      [name]: name === "displayPublicly" ? checked : value,
    }));
  };

  /**
   * Handles submit of form
   * @param {*} event submit event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setValues((prevState) => ({
        ...prevState,
        createBannerStatus: "pending",
      }));

      const bannerBlob = await downloadImage(fixUrl(currentEvent?.bannerUrl));
      const bannerBlobUrl = URL.createObjectURL(bannerBlob);

      const base64 = await generateBannerOnCanvas({
        bannerUrl: bannerBlobUrl,
        avatarImageUrl: croppedImage,
        userName: values.userName,
        imageAttribute: currentEvent.imageAttribute,
        nameAttribute: currentEvent.nameAttribute,
        avatarShape: currentEvent.imageAttribute?.shape,
      });

      URL.revokeObjectURL(bannerBlobUrl);
      const result = await uploadBase64Image(base64, "generated-banners");

      setValues((prevState) => ({
        ...prevState,
        imageUrl: result.url,
        createBannerStatus: "resolved",
      }));

      setShowDownloadButton(true);
    } catch (error) {
      console.error("Banner generation failed:", error);
      setValues((prevState) => ({
        ...prevState,
        createBannerStatus: "rejected",
      }));
    }
  };

  const updateEvent = (event) => {
    if (event) {
      incrementEvent({ variables: {
          uuid: event.uuid,
          fields: [ 'numberOfDps' ]
        } });
    }
  };

  const downloadUserBanner = async () => {
    setShowDownloadButton(false);
    updateEvent(currentEvent);
    const filename = !!values.userName
      ? values.userName?.toLocaleLowerCase().replaceAll(" ", "-")
      : "getdp";
    const blob = await downloadImage(values.imageUrl);
    saveAs(blob, `${currentEvent.url}-${filename}.jpg`);
  };

  const resetAndStartOver = () => {
    setValues(initialValuesState);
    setShowDownloadButton(false);
    setCroppedImage(null);
    setCroppedFile(null);
  };

  function createDescription() {
    return { __html: currentEvent.description };
  }

  if (loading) {
    return <Loading loading={loading} />;
  } else if (error) {
    throw new Error(
      `Event Not Found! Please check the URL in the address bar and try again.`
    );
  }

  const source = fixUrl(currentEvent.bannerUrl);
  const shape = currentEvent?.imageAttribute?.shape;

  return (
    <form
      className="relative bottom-20 md:bottom-40 w-full md:w-4/5 mx-auto text-white"
      onSubmit={handleSubmit}
    >
      <div className="bg-artfulRed">
        <div className="w-11/12  mx-auto py-12">
          <h2
            className={classNames(
              typography.sizes.h2,
              "font-poppins capitalize"
            )}
          >
            {currentEvent?.title}
          </h2>
          <p className="text-sm text-gray-200 mt-1">{currentEvent.subtitle}</p>
          <div className="my-4 flex text-gray-200">
            <p>{format(new Date(currentEvent.eventDate), "MMMM dd, yyyy")}</p>

            <p className="mx-4">|</p>
            <p>
              {currentEvent.numberOfViews} View
              {currentEvent.numberOfViews === 1 ? "" : "s"}
            </p>
          </div>

          <div className="text-sm">
            {currentEvent.numberOfDps > 0 ? (
              <p>
                {currentEvent.numberOfDps} other{" "}
                {currentEvent.numberOfDps === 1 ? "person" : "people"} already
                created their DP{currentEvent.numberOfDps === 1 ? "" : "s"}.
                Create yours now ⤵
              </p>
            ) : (
              <p>Be the first to create your DP. Create now ⤵</p>
            )}
          </div>

          {/* user banners by events */}
          <div className="border-b border-white my-4"></div>

          <div className="text-lg">
            <Description dangerouslySetInnerHTML={createDescription()} />
          </div>

          {/* Range input and crop button at the top */}
          {values?.file && !croppedImage ? (
            <EditImageControls
              zoom={zoom}
              setZoom={setZoom}
              showCroppedImage={showCroppedImage}
            />
          ) : null}

          <div className="relative w-full overflow-hidden max-w-[703px]">
            {croppedImage && (
              <img
                src={croppedImage}
                alt="user avatar"
                style={avatarCSS}
                className="cursor-pointer absolute"
              />
            )}

            {/* Image Cropper shows if there's no cropped image and user has selected an image */}
            {values?.file && !croppedImage ? (
              <>
                <Cropper
                  image={values.imagePreviewUrl}
                  crop={crop}
                  rotation={rotation}
                  zoom={zoom}
                  aspect={(currentEvent?.imageAttribute?.width || 1) / (currentEvent?.imageAttribute?.height || 1)}
                  onCropChange={setCrop}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropShape={shape === "CIRCLE" ? "round" : "rect"}
                />
              </>
            ) : values.photoUploadStatus === "pending" ? (
              <OnUploadingOverlay
                shape={shape}
                style={avatarCSS}
              >
                <ClipLoader
                  id="loader"
                  color="white"
                  loading={
                    values.photoUploadStatus === "pending" ||
                    (values.photoUploadStatus === "resolved" &&
                      source === currentEvent.bannerUrl)
                  }
                />
              </OnUploadingOverlay>
            ) : (
              <label
                htmlFor="uploadpics"
                className="cursor-pointer absolute z-10"
                style={avatarCSS}
              >
                <input
                  onChange={handlePhotoUpload}
                  type="file"
                  id="uploadpics"
                  className="hidden"
                />
                <UploadIconOverlay
                  shape={shape}
                  imageExist={Boolean(values?.file)}
                >
                  <PlusIcon
                    className={`${
                      values?.file ? "h-16 w-16" : "h-16 w-16 sm:h-32 sm:w-32"
                    }`}
                  />
                </UploadIconOverlay>
              </label>
            )}
            <img
              ref={bannerRef}
              src={source}
              alt="frame"
              className="w-full h-full object-fit align-middle"
            />
            <Content
              style={{
                ...usernameCSS,
                justifyContent:
                  currentEvent.nameAttribute?.textAlign === "rpad"
                    ? "flex-end"
                    : currentEvent.nameAttribute?.textAlign === "lpad"
                    ? "flex-start"
                    : "center",
              }}
              className="absolute text-black"
            >
              {values.userName}
            </Content>
          </div>
          {/* Range input and crop button at the bottom, just incase user missed the one at the top */}
          {values?.file && !croppedImage ? (
            <EditImageControls
              zoom={zoom}
              setZoom={setZoom}
              showCroppedImage={showCroppedImage}
            />
          ) : null}
        </div>
      </div>

      <div className="text-black w-11/12 md:w-full mx-auto my-6">
        {/* TODO clicking this should trigger Login. */}
        {/*<label className="block relative mb-2.5 pl-">*/}
        {/*  <input*/}
        {/*    type="checkbox"*/}
        {/*    className="mr-4 border border-artfulRed outline-artfulRed"*/}
        {/*    onChange={handleChange}*/}
        {/*    name="displayPublicly"*/}
        {/*    value={values.displayPublicly}*/}
        {/*  />*/}
        {/*  <span>Display my name and pictures publicly</span>*/}
        {/*</label>*/}

        <div className="flex flex-wrap md:flex-nowrap my-4 gap-2 justify-center">
          {currentEvent.nameAttribute && (
            <input
              className="p-2  border border-artfulRed mr-2 rounded-md md:basis-3/4 w-full md:w-auto outline-artfulRed"
              placeholder="Enter your name"
              onChange={handleChange}
              name="userName"
              value={values.userName}
              disabled={values?.file && !croppedImage}
            />
          )}
          <button
            className="flex bg-artfulRed items-center text-white px-5 py-2.5 rounded-md md:basis-1/4 justify-center disabled:opacity-30 disabled:cursor-not-allowed"
            type="submit"
            disabled={
              submitting ||
              values.photoUploadStatus === "pending" ||
              values.createBannerStatus === "pending" ||
              (!!currentEvent.nameAttribute && !values.userName) ||
              !croppedImage
            }
          >
            {values.createBannerStatus === "pending" || submitting ? (
              "Loading..."
            ) : (
              <>
                Generate DP <ArrowIcon className="w-5 h-5 ml-2" />
              </>
            )}
          </button>
        </div>

        <DownloadDpModal
          event={currentEvent}
          userName={values.userName}
          image={values.imageUrl}
          open={showDownloadButton}
          onDownload={downloadUserBanner}
          onClose={() => setShowDownloadButton(false)}
          onReset={resetAndStartOver}
        />

        <div>
          <div className="flex items-center justify-center text-center">
            <span className="mr-1.5">
              Not sure how to create your personalized DP
            </span>
            <span
              onClick={toggleInfoModal}
              className="cursor-pointer text-artfulRed hover:text-artfulRed-500"
            >
              <InfoIcon className="w-4 h-4" />
            </span>
          </div>

          <Modal
            open={info}
            onClose={toggleInfoModal}
            title="Creating Your Personalized DP"
            icon={InfoIcon}
            ctaBtns={{
              secondary: { text: "Got It!", onClick: toggleInfoModal },
            }}
          >
            <div className="mt-8 mx-8">
              <ol className="list-decimal">
                <li>
                  Upload your picture in the shape (square or circle) by
                  clicking/tapping the + icon.
                </li>
                <li>Enter your full name (if required)</li>
                <li>Click/tap "Generate DP"</li>
                <li>Download and share!</li>
              </ol>
            </div>
          </Modal>
        </div>
      </div>
    </form>
  );
};

export default CreateUserBannerForm;
